import axios from 'axios';
import { normalizeEmail } from '../utils/normalizeEmail';
import type { UserAccessLevel } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8090/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
  const token = localStorage.getItem(tokenKey);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface CustomerV2 {
  id: string;
  red_rock_id?: string | number; // red_rock_id field from customer_v2
  red_rock_full_entity_name?: string;
  company_name?: string;
  contact_primary_email?: string;
  invoice_email?: string;
  primary_email?: string;
  secondary_email?: string;
  primary_phone?: string;
  secondary_phone?: string;
  expand?: {
    [relationName: string]: any | any[];
  };
  [key: string]: any;
}

export interface ContactField {
  fieldName: string;
  fieldValue: string;
  displayName: string;
}

export interface CustomerWithContact {
  customerId: string;
  customerName: string;
  selectedField?: string;
  selectedValue?: string;
  availableFields: ContactField[];
}

export interface CreateUserData {
  email: string;
  username: string;
  password?: string; // Optional - for passwordless creation
  passwordConfirm?: string; // Optional - for passwordless creation
  access_level?: UserAccessLevel;
}

export interface CreateAssignmentData {
  client_id: string;
  red_rock_id: string;
  red_rock_full_entity_name: string;
  is_active: boolean;
  user_access_type: 'can_view' | 'can_edit';
  assigned_date: string;
  is_current_version: boolean;
}

class UserManagementService {
  /**
   * Fetch a single customer by ID or red_rock_id with expanded relations
   * The ID might be from a view (red_rock_id) or the actual record ID
   */
  async fetchCustomerById(customerId: string, redRockId?: string | number): Promise<CustomerV2> {
    try {
      // Try to expand all possible relations
      const expandFields = [
        'customer_profile',
        'customer_profiles',
        'contact',
        'contacts',
        'primary_contact',
        'secondary_contact',
      ].join(',');
      
      const params = new URLSearchParams({
        expand: expandFields,
      });

      // First try to fetch by record ID directly
      try {
        const response = await api.get(`/collections/customer_v2/records/${customerId}?${params.toString()}`);
        return response.data;
      } catch (error: any) {
        // If 404, try fetching by red_rock_id using filter
        if (error.response?.status === 404) {
          console.log(`⚠️ Record ID ${customerId} not found, trying red_rock_id filter...`);
          
          // Use redRockId if provided, otherwise try customerId as red_rock_id
          // The ID from the view is actually the red_rock_id, not the record ID
          const idToSearch = redRockId !== undefined ? redRockId : customerId;
          
          console.log(`🔍 Searching by red_rock_id: ${idToSearch}`);
          
          // Try with quotes (for string values)
          let filter = `red_rock_id="${idToSearch}"`;
          let filterParams = new URLSearchParams({
            filter,
            expand: expandFields,
          });
          
          let filterResponse = await api.get(`/collections/customer_v2/records?${filterParams.toString()}`);
          
          if (filterResponse.data.items && filterResponse.data.items.length > 0) {
            console.log(`✅ Found customer by red_rock_id with quotes: ${idToSearch}`);
            return filterResponse.data.items[0];
          }
          
          // If still not found, try without quotes (in case it's a number)
          filter = `red_rock_id=${idToSearch}`;
          filterParams = new URLSearchParams({
            filter,
            expand: expandFields,
          });
          
          filterResponse = await api.get(`/collections/customer_v2/records?${filterParams.toString()}`);
          
          if (filterResponse.data.items && filterResponse.data.items.length > 0) {
            console.log(`✅ Found customer by red_rock_id without quotes: ${idToSearch}`);
            return filterResponse.data.items[0];
          }
          
          throw new Error(`Customer not found with ID: ${customerId} or red_rock_id: ${idToSearch}`);
        }
        throw error;
      }
    } catch (error: any) {
      console.error('Error fetching customer by ID:', error);
      throw new Error(`Failed to fetch customer: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Fetch all customers from customer_v2 collection
   * Expands relations to get contact information
   */
  async fetchCustomers(searchTerm?: string, page: number = 1, perPage: number = 100): Promise<{ items: CustomerV2[]; totalItems: number }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        perPage: perPage.toString(),
        sort: 'red_rock_full_entity_name',
      });

      if (searchTerm && searchTerm.trim()) {
        const escapedTerm = searchTerm.trim().replace(/"/g, '\\"');
        params.append('filter', `red_rock_full_entity_name ~ "${escapedTerm}"`);
      }

      // Try to expand all possible relations - PocketBase will ignore non-existent ones
      // Common relation field names that might contain contact info
      const expandFields = [
        'customer_profile',
        'customer_profiles',
        'contact',
        'contacts',
        'primary_contact',
        'secondary_contact',
      ].join(',');
      params.append('expand', expandFields);

      const response = await api.get(`/collections/customer_v2/records?${params.toString()}`);
      return {
        items: response.data.items || [],
        totalItems: response.data.totalItems || 0,
      };
    } catch (error: any) {
      console.error('Error fetching customers:', error);
      throw new Error('Failed to fetch customers');
    }
  }

  /**
   * Get contact fields (email/phone) for a customer
   * Checks both direct fields and expanded relations
   */
  getCustomerContactFields(customer: CustomerV2): ContactField[] {
    const fields: ContactField[] = [];
    const fieldMap: { [key: string]: string } = {
      contact_primary_email: 'Contact Primary Email',
      invoice_email: 'Invoice Email',
      primary_email: 'Primary Email',
      secondary_email: 'Secondary Email',
      primary_phone: 'Primary Phone',
      secondary_phone: 'Secondary Phone',
    };

    // Debug: Log customer structure to help identify relation field names
    console.log('🔍 Customer data structure:', {
      id: customer.id,
      name: customer.red_rock_full_entity_name,
      hasExpand: !!customer.expand,
      expandKeys: customer.expand ? Object.keys(customer.expand) : [],
      directFields: Object.keys(customer).filter(k => !['id', 'expand', 'created', 'updated'].includes(k)),
    });

    // Helper function to get value from customer or expanded relations
    const getFieldValue = (fieldName: string): string | null => {
      // First check direct field
      if (customer[fieldName] && typeof customer[fieldName] === 'string') {
        return customer[fieldName].trim();
      }

      // Check expanded relations (expand object)
      if (customer.expand) {
        // Check all possible relation names
        const relationNames = Object.keys(customer.expand); // Use actual relation names from expand
        for (const relationName of relationNames) {
          const relation = customer.expand[relationName];
          if (relation) {
            // Handle both single relation and array of relations
            const relations = Array.isArray(relation) ? relation : [relation];
            for (const rel of relations) {
              if (rel && typeof rel === 'object') {
                // Check if the field exists in the relation
                if (rel[fieldName] && typeof rel[fieldName] === 'string') {
                  return rel[fieldName].trim();
                }
                // Also check nested expand if it exists
                if (rel.expand) {
                  const nestedRelations = Array.isArray(rel.expand) ? rel.expand : Object.values(rel.expand);
                  for (const nestedRel of nestedRelations) {
                    if (nestedRel && typeof nestedRel === 'object' && nestedRel[fieldName] && typeof nestedRel[fieldName] === 'string') {
                      return nestedRel[fieldName].trim();
                    }
                  }
                }
              }
            }
          }
        }
      }

      return null;
    };

    Object.keys(fieldMap).forEach((fieldName) => {
      const value = getFieldValue(fieldName);
      if (value) {
        fields.push({
          fieldName,
          fieldValue: value,
          displayName: `${fieldMap[fieldName]} (${value})`,
        });
      }
    });

    console.log('📧 Found contact fields:', fields.length, fields.map(f => f.fieldName));
    return fields;
  }

  /**
   * Generate a secure random password
   */
  private generateSecurePassword(): string {
    const length = 32;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  }

  /**
   * Create a new client_users record (passwordless)
   * Since PocketBase requires a password, we create with a temporary random password
   * that is never shown to the admin. User will set their own password via invitation email.
   */
  async createClientUser(data: CreateUserData): Promise<any> {
    const normalizedEmail = normalizeEmail(data.email);

    try {
      // Generate a secure temporary password (never shown to admin)
      // User will reset this via invitation email
      const tempPassword = this.generateSecurePassword();
      
      const userData: any = {
        email: normalizedEmail,
        username: data.username,
        password: tempPassword,
        passwordConfirm: tempPassword,
        emailVisibility: true,
        verified: false, // User must verify email and set password
        status: true,
      };

      // Only override if password is explicitly provided (for backward compatibility)
      if (data.password && data.passwordConfirm) {
        userData.password = data.password;
        userData.passwordConfirm = data.passwordConfirm;
      }

      if (data.access_level) {
        userData.access_level = data.access_level;
      }

      const response = await api.post('/collections/client_users/records', userData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating client user:', error);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Check for field-specific errors (duplicate/unique constraint violations)
        if (errorData.data && typeof errorData.data === 'object') {
          const fieldErrors: string[] = [];
          
          // Check each field for errors
          Object.keys(errorData.data).forEach((fieldName) => {
            const fieldError = errorData.data[fieldName];
            if (fieldError && typeof fieldError === 'object') {
              const errorCode = fieldError.code || '';
              const errorMessage = fieldError.message || '';
              
              // Check for unique constraint violations
              if (errorCode.includes('unique') || errorCode.includes('duplicate') || 
                  errorMessage.toLowerCase().includes('already exists') ||
                  errorMessage.toLowerCase().includes('unique constraint') ||
                  errorMessage.toLowerCase().includes('duplicate')) {
                
                if (fieldName === 'email') {
                  fieldErrors.push(`Email "${normalizedEmail}" is already registered. Please use a different email address.`);
                } else if (fieldName === 'username') {
                  fieldErrors.push(`Username "${data.username}" is already taken. Please choose a different username.`);
                } else {
                  fieldErrors.push(`${fieldName}: ${errorMessage || 'This value already exists'}`);
                }
              } else {
                // Other validation errors
                fieldErrors.push(`${fieldName}: ${errorMessage || fieldError.message || 'Invalid value'}`);
              }
            }
          });
          
          if (fieldErrors.length > 0) {
            throw new Error(fieldErrors.join(' '));
          }
        }
        
        // Fallback to general error message
        throw new Error(errorData.message || 'Failed to create client user');
      }
      
      throw new Error('Failed to create client user. Please try again.');
    }
  }

  /**
   * Update a user's status (active/inactive)
   */
  async updateUserStatus(userId: string, status: boolean): Promise<any> {
    try {
      const response = await api.patch(`/collections/client_users/records/${userId}`, {
        status,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error updating user status:', error);
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.data) {
          const fieldErrors = Object.entries(errorData.data)
            .map(([field, err]: [string, any]) => `${field}: ${err.message || err}`)
            .join(', ');
          throw new Error(fieldErrors || errorData.message || 'Failed to update user status');
        }
        throw new Error(errorData.message || 'Failed to update user status');
      }
      throw new Error('Failed to update user status');
    }
  }

  /**
   * Create user_customer_assignment records
   */
  async createUserAssignments(assignments: CreateAssignmentData[]): Promise<any[]> {
    try {
      const promises = assignments.map((assignment) =>
        api.post('/collections/user_customer_assignment/records', assignment)
      );
      const responses = await Promise.all(promises);
      return responses.map((res) => res.data);
    } catch (error: any) {
      console.error('Error creating user assignments:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Failed to create user assignments');
      }
      throw new Error('Failed to create user assignments');
    }
  }

  /**
   * Check if email already exists in client_users
   */
  async checkEmailExists(email: string): Promise<boolean> {
    try {
      const normalizedEmail = normalizeEmail(email);
      const response = await api.get(`/collections/client_users/records?filter=email="${normalizedEmail}"`);
      return response.data.items && response.data.items.length > 0;
    } catch (error: any) {
      console.error('Error checking email existence:', error);
      return false;
    }
  }

  /**
   * Generate username from email
   */
  generateUsernameFromEmail(email: string): string {
    const normalizedEmail = normalizeEmail(email);
    const emailPart = normalizedEmail.split('@')[0];
    // Remove special characters and make lowercase
    return emailPart.replace(/[^a-z0-9]/gi, '').toLowerCase();
  }

  /**
   * Request password reset email (used for sending invitation)
   * This sends a password reset email that the user can use to set their password
   */
  async requestPasswordReset(email: string): Promise<void> {
    try {
      const normalizedEmail = normalizeEmail(email);
      const response = await api.post('/collections/client_users/request-password-reset', {
        email: normalizedEmail,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error requesting password reset:', error);
      if (error.response?.data) {
        const errorData = error.response.data;
        throw new Error(errorData.message || 'Failed to send password reset email');
      }
      throw new Error('Failed to send password reset email');
    }
  }

  /**
   * Fetch existing client_users (all access levels)
   */
  async fetchExistingUsers(page: number = 1, perPage: number = 50, searchTerm?: string): Promise<{ items: any[]; totalItems: number; totalPages: number }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        perPage: perPage.toString(),
        sort: '-created', // Sort by creation date, newest first (auth collections use 'created', not 'created_at')
      });

      // Build filter - no longer filtering by access_level, show all users
      let filter = '';
      
      // Add search filter if provided
      if (searchTerm && searchTerm.trim()) {
        const escapedTerm = searchTerm.trim().replace(/"/g, '\\"');
        filter = `(email ~ "${escapedTerm}" || username ~ "${escapedTerm}")`;
      }
      
      if (filter) {
        params.append('filter', filter);
      }

      const response = await api.get(`/collections/client_users/records?${params.toString()}`);
      
      return {
        items: response.data.items || [],
        totalItems: response.data.totalItems || 0,
        totalPages: response.data.totalPages || 1,
      };
    } catch (error: any) {
      console.error('Error fetching existing users:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Failed to fetch users');
      }
      throw new Error('Failed to fetch users');
    }
  }

  private async fetchClientUserIdsBySearch(searchTerm: string, maxResults: number = 200): Promise<string[]> {
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      return [];
    }

    const params = new URLSearchParams({
      page: '1',
      perPage: maxResults.toString(),
      sort: '-created',
    });
    const escapedTerm = trimmed.replace(/"/g, '\\"');
    const filter = `(email ~ "${escapedTerm}" || username ~ "${escapedTerm}" || login_id ~ "${escapedTerm}")`;
    params.append('filter', filter);

    const response = await api.get(`/collections/client_users/records?${params.toString()}`);
    const items = response.data.items || [];
    return items.map((item: any) => item.id).filter(Boolean);
  }

  /**
   * Update a user's access_level
   */
  async updateUserAccessLevel(userId: string, accessLevel: UserAccessLevel): Promise<any> {
    try {
      const response = await api.patch(`/collections/client_users/records/${userId}`, {
        access_level: accessLevel,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error updating user access level:', error);
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.data) {
          const fieldErrors = Object.entries(errorData.data)
            .map(([field, err]: [string, any]) => `${field}: ${err.message || err}`)
            .join(', ');
          throw new Error(fieldErrors || errorData.message || 'Failed to update user access level');
        }
        throw new Error(errorData.message || 'Failed to update user access level');
      }
      throw new Error('Failed to update user access level');
    }
  }

  /**
   * Update a user's email address
   */
  async updateUserEmail(userId: string, email: string, currentEmail?: string): Promise<any> {
    const normalizedEmail = normalizeEmail(email);
    try {
      const response = await api.post('/admin-update-client-user-email', {
        userId,
        email: normalizedEmail,
        currentEmail,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error updating user email:', error);
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.data) {
          const fieldErrors = Object.entries(errorData.data)
            .map(([field, err]: [string, any]) => `${field}: ${err.message || err}`)
            .join(', ');
          throw new Error(fieldErrors || errorData.message || 'Failed to update user email');
        }
        throw new Error(errorData.message || 'Failed to update user email');
      }
      throw new Error('Failed to update user email');
    }
  }

  /**
   * Create an audit log entry
   */
  async createAuditLog(data: {
    collection: string;
    record_id: string;
    action: string;
    user_id?: string | null;
    old_data?: any;
    new_data?: any;
  }): Promise<any> {
    try {
      const response = await api.post('/collections/audit_logs/records', data);
      return response.data;
    } catch (error: any) {
      console.error('Error creating audit log:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Failed to create audit log');
      }
      throw new Error('Failed to create audit log');
    }
  }

  /**
   * Fetch all user-customer assignments (read-only for support users)
   * Returns assignments grouped by user
   */
  async fetchAllUserAssignments(page: number = 1, perPage: number = 50, searchTerm?: string): Promise<{ items: any[]; totalItems: number; totalPages: number }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        perPage: perPage.toString(),
        sort: '-created_at',
      });

      let filter = 'is_active=true && is_current_version=true';
      
      // Add search filter if provided (search by customer name and/or user identity)
      if (searchTerm && searchTerm.trim()) {
        const escapedTerm = searchTerm.trim().replace(/"/g, '\\"');
        const searchClauses: string[] = [
          `red_rock_full_entity_name ~ "${escapedTerm}"`,
          `red_rock_id ~ "${escapedTerm}"`,
          `client_id ~ "${escapedTerm}"`,
        ];

        try {
          const userIds = await this.fetchClientUserIdsBySearch(searchTerm);
          if (userIds.length > 0) {
            const userIdFilter = userIds.map((id) => `client_id = "${id}"`).join(' || ');
            searchClauses.push(`(${userIdFilter})`);
          }
        } catch (error: any) {
          console.error('Error searching client_users for assignments:', error);
        }

        filter += ` && (${searchClauses.join(' || ')})`;
      }
      
      params.append('filter', filter);
      // Only expand client_id - support users can't read customer_v2, so we'll fetch customer names separately if needed
      params.append('expand', 'client_id');

      const response = await api.get(`/collections/user_customer_assignment/records?${params.toString()}`);
      
      return {
        items: response.data.items || [],
        totalItems: response.data.totalItems || 0,
        totalPages: response.data.totalPages || 1,
      };
    } catch (error: any) {
      console.error('Error fetching user assignments:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Failed to fetch user assignments');
      }
      throw new Error('Failed to fetch user assignments');
    }
  }

  /**
   * Fetch user assignments for a specific user
   */
  async fetchUserAssignmentsByUserId(userId: string): Promise<any[]> {
    try {
      const filter = `client_id="${userId}" && is_active=true && is_current_version=true`;
      const response = await api.get(`/collections/user_customer_assignment/records?filter=${encodeURIComponent(filter)}&expand=red_rock_id`);
      return response.data.items || [];
    } catch (error: any) {
      console.error('Error fetching user assignments by user ID:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Failed to fetch user assignments');
      }
      throw new Error('Failed to fetch user assignments');
    }
  }

  /**
   * Update an existing user_customer_assignment record
   */
  async updateAssignment(assignmentId: string, data: Partial<CreateAssignmentData>): Promise<any> {
    try {
      // Fetch current record to get required fields if not provided in update data
      let currentRecord: any = null;
      try {
        const currentResponse = await api.get(`/collections/user_customer_assignment/records/${assignmentId}`);
        currentRecord = currentResponse.data;
      } catch (fetchError) {
        console.warn('Could not fetch current record, proceeding with update anyway');
      }

      // Build payload with required fields
      const payload: any = {};
      
      // Include required fields from current record if not in update data
      if (currentRecord) {
        if (!data.hasOwnProperty('red_rock_id') && currentRecord.red_rock_id) {
          payload.red_rock_id = currentRecord.red_rock_id;
        }
        if (!data.hasOwnProperty('assigned_date') && currentRecord.assigned_date) {
          payload.assigned_date = currentRecord.assigned_date;
        }
      }
      
      // Add fields from update data
      if (data.is_active !== undefined) {
        payload.is_active = data.is_active;
      }
      if (data.user_access_type !== undefined) {
        payload.user_access_type = data.user_access_type;
      }
      if (data.is_current_version !== undefined) {
        payload.is_current_version = data.is_current_version;
      }
      if (data.red_rock_id !== undefined) {
        payload.red_rock_id = data.red_rock_id;
      }
      if (data.assigned_date !== undefined) {
        payload.assigned_date = data.assigned_date;
      }
      
      // Use direct PATCH endpoint (is_active is no longer required in schema)
      const response = await api.patch(`/collections/user_customer_assignment/records/${assignmentId}`, payload);
      
      return response.data;
    } catch (error: any) {
      console.error('Error updating assignment:', error);
      if (error.response?.data) {
        const errorData = error.response.data;
        // Provide more detailed error message
        if (errorData.data) {
          const fieldErrors = Object.entries(errorData.data)
            .map(([field, err]: [string, any]) => `${field}: ${err.message || err}`)
            .join(', ');
          throw new Error(fieldErrors || errorData.message || 'Failed to update assignment');
        }
        throw new Error(errorData.message || 'Failed to update assignment');
      }
      throw new Error('Failed to update assignment');
    }
  }

  /**
   * Delete (deactivate) a user_customer_assignment record
   * Instead of hard delete, we set is_active=false
   */
  async deleteAssignment(assignmentId: string): Promise<void> {
    try {
      await api.patch(`/collections/user_customer_assignment/records/${assignmentId}`, {
        is_active: false,
      });
    } catch (error: any) {
      console.error('Error deleting assignment:', error);
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Failed to delete assignment');
      }
      throw new Error('Failed to delete assignment');
    }
  }

  /**
   * Fetch all customers for assignment selection
   */
  async fetchCustomersForAssignment(searchTerm?: string): Promise<CustomerV2[]> {
    try {
      const params = new URLSearchParams({
        perPage: '500', // Get more customers for selection
        sort: 'red_rock_full_entity_name',
      });

      if (searchTerm && searchTerm.trim()) {
        const escapedTerm = searchTerm.trim().replace(/"/g, '\\"');
        params.append('filter', `red_rock_full_entity_name ~ "${escapedTerm}"`);
      }

      const response = await api.get(`/collections/customer_v2/records?${params.toString()}`);
      return response.data.items || [];
    } catch (error: any) {
      console.error('Error fetching customers for assignment:', error);
      throw new Error('Failed to fetch customers');
    }
  }
}

export const userManagementService = new UserManagementService();

